/**
 * CompanySettings — Tenant-level metadata (name, slug, adequacy mode).
 *
 * Route: /#/settings/company
 *
 * Access: managers + executives only.
 * Reads the current user's tenant from session.tenantId, loads the matching
 * tenants row, and lets a manager edit name/slug. Push adequacy mode is shown
 * read-only because changing it has downstream cron implications.
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { tokens } from "@/components/operator/tokens";
import { getSession, supabase } from "@/lib/session";

type TenantRow = {
  id: number;
  name: string;
  slug: string | null;
  is_demo: boolean;
  push_adequacy_mode: string | null;
  created_at: string;
};

export default function CompanySettings() {
  const session = getSession();
  const allowed = useMemo(() => {
    const r = session?.role || "";
    return r === "manager" || r === "executive";
  }, [session]);

  const [tenant, setTenant] = useState<TenantRow | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session) { setLoading(false); return; }
      try {
        const sb = supabase();
        const { data, error } = await sb
          .from("tenants")
          .select("id, name, slug, is_demo, push_adequacy_mode, created_at")
          .eq("id", session.tenantId)
          .single();
        if (error) throw new Error(error.message);
        if (!cancelled && data) {
          setTenant(data as TenantRow);
          setName(data.name || "");
          setSlug(data.slug || "");
        }
      } catch (err: any) {
        if (!cancelled) setMsg({ kind: "err", text: err?.message || "Could not load company." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.tenantId]);

  async function save() {
    if (!tenant) return;
    if (!allowed) { setMsg({ kind: "err", text: "Only managers can edit company settings." }); return; }
    const trimmedName = name.trim();
    const trimmedSlug = slug.trim().toLowerCase();
    if (!trimmedName) { setMsg({ kind: "err", text: "Company name is required." }); return; }
    if (!/^[a-z0-9-]*$/.test(trimmedSlug)) { setMsg({ kind: "err", text: "Slug may only contain lowercase letters, numbers, and dashes." }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const sb = supabase();
      const { error } = await sb
        .from("tenants")
        .update({ name: trimmedName, slug: trimmedSlug || null })
        .eq("id", tenant.id);
      if (error) throw new Error(error.message);
      setTenant({ ...tenant, name: trimmedName, slug: trimmedSlug || null });
      setMsg({ kind: "ok", text: "Company settings saved." });
    } catch (err: any) {
      setMsg({ kind: "err", text: err?.message || "Could not save." });
    } finally {
      setBusy(false);
    }
  }

  const breadcrumbs = [
    { label: "Home", href: "/" },
    { label: "Settings" },
    { label: "Company" },
  ];

  return (
    <OperatorShell
      breadcrumbs={breadcrumbs}
      pageTitle="Company Settings"
      pageMeta="Tenant name, slug, and environment flags"
    >
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 720px)", gap: 16 }}>
        {!session && (
          <Card title="Sign in required">
            <p style={hintStyle}>Sign in to view company settings.</p>
          </Card>
        )}

        {session && !allowed && (
          <Card title="Restricted">
            <p style={hintStyle}>
              Only managers and executives can view or change company settings.
              Reach out to your manager if something here needs to change.
            </p>
          </Card>
        )}

        {session && allowed && (
          <>
            <Card title="Profile">
              {loading ? (
                <p style={hintStyle}>Loading…</p>
              ) : tenant ? (
                <>
                  <Row label="Tenant ID"><ReadOnly value={String(tenant.id)} /></Row>
                  <Row label="Environment">
                    <ReadOnly value={tenant.is_demo ? "Demo (test data only)" : "Production"} />
                  </Row>
                  <Row label="Company name">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      style={inputStyle}
                      placeholder="Your Company Name"
                    />
                  </Row>
                  <Row label="URL slug">
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      style={inputStyle}
                      placeholder="company-slug"
                    />
                  </Row>
                  <p style={hintStyle}>
                    The slug is reserved for future workspace URLs (e.g. mi6group.com/c/your-slug). Lowercase letters, numbers, and dashes only.
                  </p>
                  <Row label="Created">
                    <ReadOnly value={new Date(tenant.created_at).toLocaleString()} />
                  </Row>
                  <ActionRow>
                    <PrimaryBtn onClick={save} disabled={busy}>
                      {busy ? "Saving…" : "Save changes"}
                    </PrimaryBtn>
                    {msg && <InlineMsg kind={msg.kind}>{msg.text}</InlineMsg>}
                  </ActionRow>
                </>
              ) : (
                <p style={hintStyle}>No tenant record found for your session.</p>
              )}
            </Card>

            <Card title="Push defense adequacy">
              <Row label="Mode">
                <ReadOnly value={tenant?.push_adequacy_mode || "—"} />
              </Row>
              <p style={hintStyle}>
                Determines whether managers must approve push-defense answers or reps can self-check. Changing this affects cron workflows, so it is locked from this panel. To change it, contact Attilius Support · Mi6 Group through the Help menu.
              </p>
            </Card>
          </>
        )}
      </div>
    </OperatorShell>
  );
}

/* ---------- UI primitives ---------- */

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: "#FFFFFF",
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        padding: 20,
      }}
    >
      <h2 style={{ margin: "0 0 12px", fontFamily: tokens.font.display, fontSize: 16, fontWeight: 700 }}>
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gridTemplateColumns: "180px 1fr", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 13, color: tokens.color.textMuted }}>{label}</span>
      <div>{children}</div>
    </label>
  );
}

function ReadOnly({ value }: { value: string }) {
  return (
    <div style={{ fontSize: 13, color: tokens.color.textPrimary, padding: "8px 0" }}>{value}</div>
  );
}

function ActionRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", paddingTop: 4 }}>
      {children}
    </div>
  );
}

function PrimaryBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: tokens.color.black,
        color: "#FFFFFF",
        border: "none",
        borderRadius: 6,
        padding: "8px 14px",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function InlineMsg({ kind, children }: { kind: "ok" | "err"; children: React.ReactNode }) {
  const isOk = kind === "ok";
  return (
    <span style={{
      fontSize: 12,
      color: isOk ? tokens.color.success : tokens.color.danger,
      background: isOk ? tokens.color.successBg : tokens.color.dangerBg,
      padding: "4px 10px",
      borderRadius: 4,
    }}>
      {children}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 360,
  padding: "8px 10px",
  border: `1px solid ${tokens.color.border}`,
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "inherit",
  background: "#FFFFFF",
  color: tokens.color.textPrimary,
};

const hintStyle: React.CSSProperties = {
  margin: "4px 0 0",
  fontSize: 12,
  color: tokens.color.textMuted,
};
